import React, { useState, useEffect } from 'react';
import { moduleClientFactory } from '../../../api-service/module-client-factory';
import { LookupAutocomplete } from '../components/lookup-autocomplete';
import { ICompanyLookup, ICompanyLookupProps } from './interfaces';
import { formatCompany, renderCompany } from './formatters';
import { CompanyLookupModel, CompanyModel } from '../../../api-service/clients';
import { ReadonlyInput } from '../../readonly-input/readonly-input';

const LookupsClient = moduleClientFactory.createLookupCompanyClient();

export function CompanyLookup(props: ICompanyLookupProps) {
  const { onChange, value, fetchMinimumInputLength = 2, label = 'Virksomhed', placeholder = 'Fremsøg virksomhed', readOnly, initialData, ...rest } = props;

  const [selectedValue, setSelectedValue] = useState<CompanyModel>({} as CompanyModel);
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<ICompanyLookup[]>(initialData || []);
  const [isSearchLoading, setIsSearchLoading] = useState(false);

  useEffect(() => {
    async function search() {
      setIsSearchLoading(true);
      try {
        const response = await LookupsClient.getCompaniesByLookupQuery({ query: searchValue });
        setData(response.companies || []);
      } catch {
        setData([]);
      } finally {
        setIsSearchLoading(false);
      }
    }

    if (searchValue.length >= fetchMinimumInputLength) {
      search();
    } else {
      setData(initialData || []);
    }
  }, [searchValue]);

  useEffect(() => {
    async function getSingle() {
      if (!value) {
        setSelectedValue({} as CompanyLookupModel);
        return;
      }
      try {
        setIsLoading(true);
        const response = await LookupsClient.getCompanyByIdForLookup({ companyId: value });
        if (response?.company) {
          setSelectedValue(response.company);
        }
      } finally {
        setIsLoading(false);
      }
    }
    getSingle();
  }, [value]);

  function handleOnChange(selected: ICompanyLookup) {
    onChange?.(selected);
    setSelectedValue(selected);
  }

  function handleOpenCompany(companyId: string) {
    window.open(`/company/${companyId}/details`);
  }

  return readOnly ? (
    <ReadonlyInput label={label} value={formatCompany(selectedValue)} onOpenEntity={selectedValue.id ? () => handleOpenCompany(selectedValue.id) : undefined} />
  ) : (
    <LookupAutocomplete<ICompanyLookup>
      {...rest}
      label={label}
      placeholder={placeholder}
      isOptionEqualToValue={(option, val) => option?.id === val?.id}
      getOptionLabel={formatCompany}
      renderOption={renderCompany}
      onChange={handleOnChange as never}
      value={selectedValue}
      onSearchValueChange={setSearchValue}
      data={data}
      isLoading={isSearchLoading || isLoading}
      tooltipTitle={formatCompany(selectedValue)}
    />
  );
}
